/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { i18n } from 'config/locales';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonRequired, CommonOnError
} from 'types/props';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import Field from 'components/Form/Field';
import Text from 'components/Form/Text';
import Branch from 'components/Form/Branch';
import DateControl from 'components/Form/DateControl';
import PVQLocation from 'components/Form/PVQLocation';
import { CitizenshipForeignPassportItemModel } from 'forms/sections/citizenship/multiple/types';
import PVQName from 'components/Form/PVQName';
import BranchCollection from 'components/Form/BranchCollection';
import Country from 'components/Form/Country';
import Textarea from 'components/Form/Textarea';
import CitizenshipIntlItem from './CitizenshipIntlItem';

interface Props extends Partial<CitizenshipForeignPassportItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  attribute: string
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  errors?: []
  required?: CommonRequired
}

export default class CitizenshipForeignPassportItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
  }

  static defaultProps: Partial<Props> = {
    onUpdate: () => { },
    errors: [],
    scrollIntoView: false,
    onError: () => { },
    required: false,
  };

  update = (queue) => {
    this.props.onUpdate({
      Has: this.props.Has,
      International: this.props.International,
      DocumentNum: this.props.DocumentNum,
      NameUsed: this.props.NameUsed,
      IssueLocation: this.props.IssueLocation,
      ExpirationDate: this.props.ExpirationDate,
      Access: this.props.Access,
      IssueDate: this.props.IssueDate,
      ForeignCountry: this.props.ForeignCountry,
      DocIntent: this.props.DocIntent,
      DocIntentExplain: this.props.DocIntentExplain,
      DocIntentIntl: this.props.DocIntentIntl,
      DocIntentIntlExplain: this.props.DocIntentIntlExplain,
      ...queue,
    });
  };

  updateDocIntent = (values: CitizenshipForeignPassportItemModel['DocIntent']) => {
    this.update({
      DocIntent: values,
    });
  };

  updateDocIntentExplain = (values: CitizenshipForeignPassportItemModel['DocIntentExplain']) => {
    this.update({
      DocIntentExplain: values,
    });
  };

  updateDocIntentIntl = (values: CitizenshipForeignPassportItemModel['DocIntentIntl']) => {
    this.update({
      DocIntentIntl: values,
    });
  };

  updateDocIntentIntlExplain = (values: CitizenshipForeignPassportItemModel['DocIntentIntlExplain']) => {
    this.update({
      DocIntentIntlExplain: values,
    });
  };

  updateForeignCountry = (values: CitizenshipForeignPassportItemModel['ForeignCountry']) => {
    this.update({
      ForeignCountry: values,
    });
  };

  updateInternational = (values: CitizenshipForeignPassportItemModel['International']) => {
    this.update({
      International: values,
    });
  };

  updateDocumentNum = (values: CitizenshipForeignPassportItemModel['DocumentNum']) => {
    this.update({
      DocumentNum: values,
    });
  };

  updateNameUsed = (values: CitizenshipForeignPassportItemModel['NameUsed']) => {
    this.update({
      NameUsed: values,
    });
  };

  updateIssueLocation = (values: CitizenshipForeignPassportItemModel['IssueLocation']) => {
    this.update({
      IssueLocation: values,
    });
  };

  updateExpirationDate = (values: CitizenshipForeignPassportItemModel['ExpirationDate']) => {
    this.update({
      ExpirationDate: values,
    });
  };

  updateAccess = (values: CitizenshipForeignPassportItemModel['Access']) => {
    this.update({
      Access: values,
    });
  };

  updateIssueDate = (values: CitizenshipForeignPassportItemModel['IssueDate']) => {
    this.update({
      IssueDate: values,
    });
  };

  render() {
    const { errors } = this.props;
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('ForeignPassportList.accordion') === 0);

    return (
      <>
        <Field
          title={i18n.t('citizenship.pvq.additional.heading.citizenship.foreignCountry')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ForeignCountry`}
        >
          <Country
            name="ForeignCountry"
            {...this.props.ForeignCountry}
            attribute={`${attribute}.ForeignCountry`}
            className="citizenship-country"
            onUpdate={this.updateForeignCountry}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Branch
          name="Access"
          {...this.props.Access}
          attribute={`${attribute}.Access`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.access')}
          labelSize="h4"
          className="citizenship-access"
          onUpdate={this.updateAccess}
          onError={this.props.onError}
          required={this.props.required}
        />
        {this.props.Access?.value === 'Yes'
          && (
            <>
              <Field
                title={i18n.t('citizenship.pvq.additional.heading.citizenship.issueDate')}
                attribute={`${attribute}.IssueDate`}
                titleSize="h4"
                scrollIntoView={this.props.scrollIntoView}
              >
                <DateControl
                  name="IssueDate"
                  attribute={`${attribute}.IssueDate`}
                  {...this.props.IssueDate}
                  className="citizenship-issue-date-pvq"
                  onUpdate={this.updateIssueDate}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
              <Field
                title={i18n.t('citizenship.pvq.additional.heading.citizenship.expirationDate')}
                attribute={`${attribute}.ExpirationDate`}
                titleSize="h4"
                scrollIntoView={this.props.scrollIntoView}
              >
                <DateControl
                  name="ExpirationDate"
                  attribute={`${attribute}.ExpirationDate`}
                  {...this.props.ExpirationDate}
                  className="citizenship-expire-date-pvq"
                  onUpdate={this.updateExpirationDate}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
              <Field
                title={i18n.t('citizenship.pvq.additional.heading.citizenship.issueLocation')}
                attribute={`${attribute}.IssueLocation`}
                titleSize="h4"
                scrollIntoView={this.props.scrollIntoView}
              >
                <PVQLocation
                  {...this.props.IssueLocation}
                  attribute={`${attribute}.IssueLocation`}
                  layout="City, Country"
                  onUpdate={this.updateIssueLocation}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
              <PVQName
                {...this.props.NameUsed}
                title={i18n.t('citizenship.pvq.additional.heading.citizenship.nameUsed')}
                attribute={`${attribute}.NameUsed`}
                onUpdate={this.updateNameUsed}
                onError={this.props.onError}
                required={this.props.required}
              />
              <Field
                title={i18n.t('citizenship.pvq.additional.heading.citizenship.docNum')}
                attribute={`${attribute}.DocumentNum`}
                titleSize="h4"
                scrollIntoView={this.props.scrollIntoView}
              >
                <Text
                  name="DocumentNum"
                  attribute={`${attribute}.DocumentNum`}
                  {...this.props.DocumentNum}
                  className="citizenship-doc-number-pvq"
                  onUpdate={this.updateDocumentNum}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
            </>
          )}
        <BranchCollection
          {...this.props.International}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.usedForIntl')}
          labelSize="h4"
          appendLabel={i18n.t('citizenship.pvq.additional.label.citizenship.appendLabelIntlDoc')}
          className="citizenship-international-passport"
          onUpdate={this.updateInternational}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.International`}
        >
          <CitizenshipIntlItem
            name="Item"
            bind
            attribute={`${attribute}.International`}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </BranchCollection>
        <Branch
          name="DocIntent"
          {...this.props.DocIntent}
          attribute={`${attribute}.DocIntent`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.docIntent')}
          labelSize="h4"
          className="citizenship-doc-intent"
          onUpdate={this.updateDocIntent}
          onError={this.props.onError}
          required={this.props.required}
        />
        {this.props.DocIntent?.value === 'No' && (
          <Field
            title={i18n.t('citizenship.pvq.additional.label.explain')}
            attribute={`${attribute}.DocIntentExplain`}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="DocIntentExplain"
              attribute={`${attribute}.DocIntentExplain`}
              {...this.props.DocIntentExplain}
              className="citizenship-doc-intent-explain"
              onUpdate={this.updateDocIntentExplain}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}
        <Branch
          name="DocIntentIntl"
          {...this.props.DocIntentIntl}
          attribute={`${attribute}.DocIntentIntl`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.docIntentIntl')}
          labelSize="h4"
          className="citizenship-doc-intent-intl"
          onUpdate={this.updateDocIntentIntl}
          onError={this.props.onError}
          required={this.props.required}
        />
        {this.props.DocIntentIntl?.value === 'Yes' && (
          <Field
            title={i18n.t('citizenship.pvq.additional.label.explain')}
            attribute={`${attribute}.DocIntentIntlExplain`}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="DocIntentIntlExplain"
              attribute={`${attribute}.DocIntentIntlExplain`}
              {...this.props.DocIntentIntlExplain}
              className="citizenship-doc-intent-intl-explain"
              onUpdate={this.updateDocIntentIntlExplain}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}
      </>
    );
  }
}

CitizenshipForeignPassportItem.defaultProps = {
  ForeignCountryFocused: false
};
